import React, { useState, Fragment } from 'react';
import { Check, ChevronDown, Sparkles, Calendar, Mail } from 'lucide-react';
import { PageHeader } from '../components/page/PageHeader';
import { Reveal } from '../components/ui/Reveal';
import { Button } from '../components/ui/Button';
import { MarketplaceModal } from '../components/MarketplaceModal';
import { HowMarketplaceWorks } from '../components/page/HowMarketplaceWorks';
interface Tier {
  name: string;
  desc: string;
  monthly: string;
  annual: string;
  note: string;
  cta: string;
  features: string[];
  highlight: boolean;
}
const tiers: Tier[] = [{
  name: 'Casting',
  desc: 'Post casting calls and receive custom submissions',
  monthly: '$149',
  annual: '$125',
  note: '40% off Beta pricing',
  cta: 'Try Casting',
  features: ['30-day free trial', 'Post unlimited casting calls', 'Access a network of 400+ talent agencies', 'Receive talent submissions & quotes from talent agencies', 'Live influencer metrics & audience demographic data', 'Direct talent manager contact details', 'Search & filter submissions', '3 users'],
  highlight: false
}, {
  name: 'Marketplace',
  desc: 'Search and discover represented talent',
  monthly: '$239',
  annual: '$199',
  note: '40% off Beta pricing',
  cta: 'Request Marketplace Access',
  features: ['Approval required', '30-day free trial', 'Everything in Casting', 'Search 6,000 represented influencers', 'Browse 400+ talent agency rosters', 'Marketplace insights & pricing benchmarks', 'Consulting sessions', 'Unlimited users', 'Priority customer support'],
  highlight: true
}];
const faqs = [{
  q: 'How does the free trial work?',
  a: 'Both Casting and Marketplace plans come with a 30-day free trial so you can explore the platform and post your first casting call risk-free.'
}, {
  q: 'Why does Marketplace require approval?',
  a: 'The roster database and talent agency directory contain sensitive contact and pricing information. We vet users to ensure a high-quality environment for our talent agencies.'
}, {
  q: 'Can I switch plans later?',
  a: 'Anytime. Plans are monthly or annual, and you can upgrade or downgrade whenever your needs change.'
}, {
  q: 'Who owns the creator data?',
  a: 'Talent agencies own and manage their creators’ profiles. Brands and agencies always see live, agency-maintained data that stays current.'
}];
const stats = ['400+ Talent Agencies', '6,000+ Represented Influencers', '$500K+ GMV Facilitated'];
const FaqItem = ({
  q,
  a
}: {
  q: string;
  a: string;
}) => {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-gray-200 py-5" data-id="element-2112">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left gap-4" data-id="element-2113">
        <span className="font-semibold text-gray-900" data-id="element-2114">{q}</span>
        <ChevronDown size={20} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-2115" />
      </button>
      {open && <p className="mt-3 text-gray-600 leading-relaxed" data-id="element-2116">{a}</p>}
    </div>;
};
export const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [isMarketplaceModalOpen, setIsMarketplaceModalOpen] = useState(false);
  return <>
      <MarketplaceModal isOpen={isMarketplaceModalOpen} onClose={() => setIsMarketplaceModalOpen(false)} data-id="element-2117" />
      <PageHeader eyebrow="For Brands & Marketing Agencies" title="Get direct access to hundreds of talent agencies" subtitle="Discover talent, post casting calls and receive curated submissions directly from influencer talent agencies." data-id="element-2118" />

      {/* Stats + beta banner */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-10 text-center" data-id="element-2119">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-medium text-gray-500" data-id="element-2120">
          {stats.map((s, i) => <Fragment key={s} data-id="element-2121">
              <span data-id="element-2122">{s}</span>
              {i < stats.length - 1 && <span className="text-gray-300" aria-hidden="true" data-id="element-2123">
                  |
                </span>}
            </Fragment>)}
        </div>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-zano-lavender/40 border border-zano-pink/20 px-4 py-2 text-sm" data-id="element-2124">
          <Sparkles size={16} className="text-zano-pink" data-id="element-2125" />
          <span className="text-gray-700" data-id="element-2126">
            Beta Pricing —{' '}
            <span className="font-semibold text-zano-pink" data-id="element-2127">
              Lock in 40% off
            </span>{' '}
            for life
          </span>
        </div>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-12" data-id="element-2128">
        <span className={`text-sm font-semibold ${annual ? 'text-zano-charcoal' : 'text-gray-400'}`} data-id="element-2129">
          Annual{' '}
          <span className={annual ? 'text-zano-pink' : 'text-gray-400'} data-id="element-2130">
            (save 20%)
          </span>
        </span>
        <button onClick={() => setAnnual(!annual)} aria-label="Toggle annual billing" className="relative w-12 h-6 rounded-full bg-zano-pink/20 transition-colors" data-id="element-2131">
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-zano-pink shadow transition-transform ${!annual ? 'translate-x-6' : ''}`} data-id="element-2132" />
        </button>
        <span className={`text-sm font-semibold ${!annual ? 'text-zano-charcoal' : 'text-gray-400'}`} data-id="element-2133">
          Monthly
        </span>
      </div>

      {/* Tiers */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2134">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-2135">
          {tiers.map((t, i) => <Reveal key={t.name} delay={i * 0.08} className="h-full" data-id="element-2136">
              <div className={`relative h-full flex flex-col rounded-2xl border p-7 ${t.highlight ? 'border-zano-pink shadow-float bg-white' : 'border-gray-200 bg-white shadow-sm'}`} data-id="element-2137">
                {t.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zano-pink text-white text-xs font-semibold px-3 py-1 rounded-full" data-id="element-2138">
                    Most Popular
                  </span>}
                <h3 className="text-lg font-bold text-gray-900" data-id="element-2139">{t.name}</h3>
                <p className={`text-sm text-gray-500 mb-5 ${t.name === 'Casting' ? 'md:whitespace-nowrap' : ''}`} data-id="element-2140">
                  {t.desc}
                </p>
                <div className="mb-1 flex items-end gap-1" data-id="element-2141">
                  <span className="text-4xl font-bold text-zano-charcoal" data-id="element-2142">
                    {annual ? t.annual : t.monthly}
                  </span>
                  <span className="text-sm text-gray-500 mb-1" data-id="element-2143">
                    {annual ? '/ month, billed annually' : '/ month'}
                  </span>
                </div>
                <p className="text-sm font-semibold text-zano-pink mb-6" data-id="element-2144">
                  {t.note}
                </p>
                {t.name === 'Marketplace' ? <Button variant={t.highlight ? 'primary' : 'secondary'} className="w-full justify-center" onClick={() => setIsMarketplaceModalOpen(true)} data-id="element-2145">
                    {t.cta}
                  </Button> : <a href="https://app.zanoplatform.com/get-started?tier=explorer&type=brand&cycle=monthly" className="block" data-id="element-2146">
                    <Button variant={t.highlight ? 'primary' : 'secondary'} className="w-full justify-center" data-id="element-2147">
                      {t.cta}
                    </Button>
                  </a>}
                <ul className="mt-7 space-y-3" data-id="element-2148">
                  {t.features.map(f => <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700" data-id="element-2149">
                      <Check size={16} className="text-zano-pink mt-0.5 shrink-0" data-id="element-2150" />{' '}
                      {f}
                    </li>)}
                </ul>
              </div>
            </Reveal>)}
        </div>
      </section>

      <HowMarketplaceWorks audience="brands" background="white" data-id="element-2151" />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2152">
        <Reveal data-id="element-2153">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8" data-id="element-2154">
            Frequently asked questions
          </h2>
          <div data-id="element-2155">
            {faqs.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-2156" />)}
          </div>
        </Reveal>
      </section>

      {/* Bottom CTA */}
      <section className="py-20" data-id="element-2157">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2158">
          <Reveal data-id="element-2159">
            <div className="bg-zano-charcoal rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden" data-id="element-2160">
              <div className="absolute top-0 right-0 w-64 h-64 bg-zano-pink/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2161" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-zano-lavender/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-2162" />
              <div className="relative z-10 max-w-2xl mx-auto" data-id="element-2163">
                <h2 className="text-3xl md:text-4xl font-bold mb-5" data-id="element-2164">
                  Not sure which plan fits?
                </h2>
                <p className="text-lg text-gray-300 mb-8" data-id="element-2165">
                  Book a demo with us, or ask us anything by emailing{' '}
                  <a href="mailto:support@zanoplatform.com" className="text-white font-semibold underline underline-offset-2 hover:text-zano-pink transition-colors" data-id="element-2166">
                    support@zanoplatform.com
                  </a>
                  .
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2167">
                  <a href="mailto:support@zanoplatform.com?subject=Book%20a%20demo%20with%20Zano" className="w-full sm:w-auto" data-id="element-2168">
                    <Button variant="primary" className="bg-white text-zano-charcoal hover:bg-gray-100 w-full sm:w-auto px-8 py-3" data-id="element-2169">
                      <Calendar size={18} className="mr-1.5" data-id="element-2170" />
                      Book a Demo
                    </Button>
                  </a>
                  <a href="mailto:support@zanoplatform.com" className="w-full sm:w-auto" data-id="element-2171">
                    <Button variant="secondary" className="bg-transparent text-white border-gray-600 hover:bg-gray-800 hover:border-gray-500 w-full sm:w-auto px-8 py-3" data-id="element-2172">
                      <Mail size={18} className="mr-1.5" data-id="element-2173" />
                      Email Support
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>;
};